import { createFileRoute } from "@tanstack/react-router";
import { ContentPage } from "@/components/ContentPage";
import { FaqSection, faqJsonLd, type FaqItem } from "@/components/FaqSection";
import { SakerhetsfilmShowcase } from "@/components/sections/SakerhetsfilmShowcase";

import { IMAGES, BBN_PHOTOS } from "@/lib/images";

export const FAQS: FaqItem[] = [
  {
    q: "Vilken säkerhetsfilm rekommenderas för en kommersiell fastighet?",
    a: "För flerbostadshus, kontors- och handelsfastigheter rekommenderar vi 3M Ultra S800 eller 3M Safety S140 klassad enligt EN 12600 klass 2B2 eller 1B1. Ultra S800 är vår standard för fastigheter i markplan där både splitter- och inbrottsskydd krävs, medan Safety S140 används i övre plan där splitterskydd mot personskada är huvudsyftet.",
  },
  {
    q: "Vad kostar säkerhetsfilm till en hel fastighet?",
    a: "Priset styrs av glasyta, filmklass, arbetstidshöjd och tillgänglighet. Som riktvärde ligger säkerhetsfilm för fastigheter mellan 750–1 400 kr/m² inklusive material och installation. Efter platsbesöket får du ett fast pris per etapp, så att du kan budgetera per våningsplan eller trappuppgång.",
  },
  {
    q: "Hjälper säkerhetsfilm mot glaskross vid vandalism och stenkastning?",
    a: "Ja. Splitterskyddsfilm håller ihop rutan även när glaset spricker, så att skärvor inte trillar in i lokalen eller ut på gata/innergård. Det förhindrar följdskador på personer och egendom och gör att fastighetsägaren kan låta rutan sitta kvar tills byte sker planerat i stället för akut.",
  },
  {
    q: "Vilka lagkrav gäller för säkerhetsfilm i offentliga fastigheter?",
    a: "Boverkets byggregler (BBR 8:35) kräver skydd mot personskador vid glasyta i publika lokaler. AFS 1998:5 och AFS 2020:1 ställer krav på arbetsmiljö i lokaler där personal vistas. För skolor, sjukhus och offentliga entréer används vanligen EN 12600 klass 1B1 eller 2B2 — vi tar fram en klassificeringsbedömning per glasparti i offerten.",
  },
  {
    q: "Kan installationen ske utan att verksamheten i fastigheten störs?",
    a: "Ja. Vi tar det våning för våning och följer förvaltarens driftschema. Filmen är dammfri i montage och lokalen kan användas direkt efteråt. För större fastigheter (2 000+ m² glas) levererar vi projektplan över 3–6 veckor med etappvis fakturering.",
  },
  {
    q: "Påverkar säkerhetsfilm försäkringspremien för fastigheten?",
    a: "Flera försäkringsbolag ger premiereduktion när säkerhetsfilm är installerad tillsammans med larm och kamera. Vi levererar ett installationsintyg med filmklass, EN 12600-klassificering och auktoriserad installatörsstämpel som fastighetsägaren kan skicka till sitt försäkringsbolag.",
  },
];

export const Route = createFileRoute("/sakerhetsfilm-fastighet")({
  head: () => ({
    meta: [
      { title: "Säkerhetsfilm för fastighet — splitterskydd, inbrott & EN 12600 | BBN Nordic" },
      {
        name: "description",
        content:
          "Säkerhetsfilm och splitterskyddsfilm för fastigheter i Skåne och Göteborg. 3M Ultra & Safety enligt EN 12600, ID06-ansluten installation och installationsintyg till försäkringsbolaget. Fast pris per etapp.",
      },
      { property: "og:title", content: "Säkerhetsfilm för fastighet — BBN Nordic" },
      {
        property: "og:description",
        content:
          "Splitterskydd, inbrottsfördröjning och personskadeskydd för kommersiella fastigheter och flerbostadshus. Fast pris per etapp, ID06-ansluten installation.",
      },
      { property: "og:url", content: "https://bbnnordic.se/sakerhetsfilm-fastighet" },
      { property: "og:image", content: BBN_PHOTOS.sakerhetsfilmKontorHero },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/sakerhetsfilm-fastighet" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Säkerhetsfilm för fastighet",
          serviceType:
            "Splitterskydd, inbrottsfördröjning och personskadeskydd för kommersiella fastigheter och flerbostadshus",
          description:
            "3M Ultra & Safety enligt EN 12600, ID06-ansluten installation och installationsintyg till försäkringsbolaget.",
          provider: { "@type": "LocalBusiness", "@id": "/#organization", name: "BBN Nordic" },
          areaServed: ["Skåne", "Göteborg", "Sverige"],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://bbnnordic.se/" },
            { "@type": "ListItem", position: 2, name: "Säkerhetsfilm", item: "https://bbnnordic.se/sakerhetsfilm" },
            { "@type": "ListItem", position: 3, name: "Säkerhetsfilm för fastighet", item: "https://bbnnordic.se/sakerhetsfilm-fastighet" },
          ],
        }),
      },
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd(FAQS)) },
    ],
  }),
  component: () => (
    <ContentPage
      disableCta
      eyebrow="BRANSCH — FASTIGHET"
      title="Säkerhetsfilm för fastighet"
      subtitle="Splitterskydd, inbrottsfördröjning och personskadeskydd för kommersiella fastigheter och flerbostadshus med auktoriserad 3M-installation med installationsintyg till försäkringsbolaget."
      heroImage={BBN_PHOTOS.sakerhetsfilmKontorHero}
      breadcrumbs={[
        { label: "Säkerhetsfilm", to: "/sakerhetsfilm" },
        { label: "Fastighet" },
      ]}
      sections={[
        {
          eyebrow: "SPLITTERSKYDD & PERSONSKADA",
          heading: "Håller ihop rutan när något går sönder.",
          paragraphs: [
            "När en ruta krossas behöver du hantera både skadan, risken för personskador och avbrottet i verksamheten. Splitterskyddsfilm håller ihop rutan även när den spricker. Skärvorna faller då inte ner på trottoaren, innergården, garageuppfarten eller i publika utrymmen.",
            "I flerbostadshus, kontorsfastigheter och handelslokaler börjar vi ofta med splitterskydd. Filmen monteras på befintligt glas utan fönsterbyte. Vi kan arbeta rum för rum utan att boende eller verksamheter behöver evakueras.",
          ],
          bullets: [
            "3M Safety S140 — 140 μm splitterskydd för övre våningsplan",
            "3M Ultra S800 — flerlagsteknik för markplan och entréer",
            "EN 12600 klass 2B2 / 1B1 — dokumenterad klassificering per glasparti",
            "Skydd mot fallande skärvor på trottoar och innergård",
            "Personskadeskydd enligt BBR 8:35 (publika lokaler)",
          ],
          image: BBN_PHOTOS.installation5,
        },
        {
          eyebrow: "INBROTT & VANDALISM",
          heading: "Säkerhetsfilm fördröjer krossförsök med 30–90 sekunder.",
          paragraphs: [
            "Säkerhetsfilm enligt EN 12600 klass 2B2 håller ihop glaset i 30–90 sekunder vid ett spontant krossförsök. Det räcker för att larmcentral eller väktare ska hinna agera och för att inkräktaren i de flesta fall avbryter försöket och rör sig vidare.",
            "För fastigheter i markplan, i utsatta områden eller med tidigare vandalism innebär det färre skador. Vid kombinationsskydd (film + larm + kamera) ger flera försäkringsbolag premiereduktion och vi levererar installationsintyg som fastighetsägaren kan skicka in.",
          ],
          bullets: [
            "Fördröjning 30–90 sekunder vid spontant krossförsök",
            "EN 12600 klass 2B2 — standardrekommendation för markplan",
            "EN 12600 klass 1B1 — för entréer, banklokaler och särskilda skyddsbehov",
            "Installationsintyg för försäkringsbolaget",
            "Referenser: fastighetsbolag, BRF-styrelser och kommersiella hyresgäster",
          ],
          image: IMAGES.sakerhetsfilmHero,
        },
        {
          eyebrow: "PROJEKT & FÖRVALTNING",
          heading: "ID06-ansluten installation som förvaltaren kan planera.",
          paragraphs: [
            "Vi arbetar med fastighetsförvaltare, tekniska chefer och BRF-styrelser i hela Skåne och Göteborgsregionen. Du får offerten uppdelad per våningsplan, trappuppgång eller fastighet. Då kan du budgetera varje etapp och fördela arbetet över flera driftsår om det behövs.",
            "All personal är ID06-ansluten, har giltig F-skatt, kollektivavtal och samtliga ansvarsförsäkringar. Vi kan delta i nybyggnation, hyresgästanpassning och löpande fastighetsunderhåll utan att huvudentreprenören behöver göra extra kontroller.",
          ],
          bullets: [
            "Fast pris per etapp — våning, trappuppgång eller hel fastighet",
            "ID06, F-skatt, kollektivavtal och ansvarsförsäkring",
            "Auktoriserad 3M-installatör — projektgaranti registreras på objektet",
            "10 års produktgaranti (3M) + 10 års arbetsgaranti (BBN)",
            "Montering våning för våning enligt förvaltarens schema",
          ],
          image: BBN_PHOTOS.buildingSun,
        },
      ]}
      related={[
        { to: "/sakerhetsfilm", label: "Säkerhetsfilm — översikt", desc: "Hela säkerhetssortimentet." },
        { to: "/splitterskydd-fonster", label: "Splitterskydd", desc: "Splitterskyddsfilm i detalj." },
        { to: "/sakerhetsfilm-butik", label: "Säkerhetsfilm för butik", desc: "Handel och skyltfönster." },
        { to: "/sakerhetsfilm-kontor", label: "Säkerhetsfilm för kontor", desc: "GDPR, EN 12600 och ID06." },
        { to: "/offert", label: "Begär offert", desc: "Underlag för fastighetsförvaltare." },
      ]}
      priorityContent={<SakerhetsfilmShowcase />}
    >
      <FaqSection
        heading="Vanliga frågor från fastighetsförvaltare och tekniska chefer."
        items={FAQS}
      />
    </ContentPage>
  ),
});
